import { useEffect, useRef, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import DialogBarcodeReader from "pages/components/DialogBarcodeReader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";

import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

export default function DialogInventarioHistoricoMaterial({ open, onClose }) {
  const { showAlert } = useAlert();
  const { userData } = useAuth();

  const idutilizador = Number(userData?.id || 0);

  const [materiais, setMateriais] = useState([]);
  const [material, setMaterial] = useState(null);
  const [textoPesquisa, setTextoPesquisa] = useState("");
  const [pesquisando, setPesquisando] = useState(false);

  const [centros, setCentros] = useState([]);
  const [centroSelecionado, setCentroSelecionado] = useState(null);
  const [loadingCentros, setLoadingCentros] = useState(false);

  const [openBarcodeReader, setOpenBarcodeReader] = useState(false);

  const [contagens, setContagens] = useState([]);
  const [loadingContagens, setLoadingContagens] = useState(false);

  const historicoSeq = useRef(0);

  const columnsHistorico = [
    {
      Header: "Data",
      accessor: "data_contagem",
      Cell: ({ value }) =>
        value
          ? new Date(value).toLocaleString("pt-PT", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
    },
    {
      Header: "Inventário",
      accessor: "inventario",
      Cell: ({ row }) => (
        <MDBox>
          <MDTypography variant="button" fontWeight="medium">
            #{row.original.idinventario} — {row.original.inventario}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block">
            {row.original.centro}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "Estado",
      accessor: "estado_inventario",
      Cell: ({ value }) => (
        <Chip
          label={value === "ABERTO" ? "Aberto" : "Encerrado"}
          size="small"
          sx={{
            backgroundColor: value === "ABERTO" ? "#e8f5e9" : "#eceff1",
            color: value === "ABERTO" ? "#2e7d32" : "#455a64",
            fontWeight: 700,
          }}
        />
      ),
    },
    {
      Header: "Operação",
      accessor: "tipo_operacao",
    },
    {
      Header: "Lote",
      accessor: "lote",
      Cell: ({ value }) => <MDTypography variant="caption">{value || "Sem lote"}</MDTypography>,
    },
    {
      Header: "Localização",
      accessor: "localizacao",
    },
    {
      Header: "Qtd. lida",
      accessor: "quantidade_lida",
    },
    {
      Header: "Qtd. convertida",
      accessor: "quantidade_stock",
    },
    {
      Header: "Qtd. final",
      accessor: "quantidade_final",
    },
    {
      Header: "Utilizador",
      accessor: "utilizador",
    },
    {
      Header: "Contagem",
      accessor: "cancelada",
      Cell: ({ row }) =>
        row.original.cancelada ? (
          <Chip label="Anulada" size="small" color="error" variant="outlined" />
        ) : (
          <Chip label="Válida" size="small" color="success" variant="outlined" />
        ),
    },
  ];

  useEffect(() => {
    if (!open || !idutilizador) return undefined;

    let ativo = true;

    const carregarCentros = async () => {
      setLoadingCentros(true);

      try {
        const [[listaCentros, erroCentros], [dadosUtilizador, erroUtilizador]] = await Promise.all([
          handleApi(api.post("Stocks/SelInventarioCentrosCombo", {})),
          handleApi(
            api.post("Stocks/SelInventarioCentroUtilizador", {
              idutilizador,
            })
          ),
        ]);

        if (!ativo) return;

        if (erroCentros) {
          showAlert(erroCentros, "error");
          setCentros([]);
          return;
        }

        const lista = Array.isArray(listaCentros) ? listaCentros : [];
        setCentros(lista);

        if (erroUtilizador) {
          showAlert(erroUtilizador, "error");
          return;
        }

        const refcentro = String(dadosUtilizador?.refcentro || "1100").trim();

        const centroInicial =
          lista.find((item) => String(item.refcentro).trim() === refcentro) || null;

        setCentroSelecionado(centroInicial);

        if (!centroInicial) {
          showAlert(`O centro ${refcentro} não foi encontrado na lista de centros.`, "warning");
        }
      } catch (error) {
        if (ativo) {
          showAlert(error?.message || "Erro ao carregar os centros.", "error");
        }
      } finally {
        if (ativo) {
          setLoadingCentros(false);
        }
      }
    };

    carregarCentros();

    return () => {
      ativo = false;
    };
  }, [open, idutilizador]);

  const pesquisarMateriais = async (texto) => {
    const q = String(texto || "").trim();

    setTextoPesquisa(texto);

    if (q.length < 2) {
      setMateriais([]);
      return;
    }

    setPesquisando(true);

    const [data, err] = await handleApi(
      api.post("Pesquisa/QuickSearch", {
        q,
        limit: 30,
        refvendedor: "",
        idutilizador,
      })
    );

    setPesquisando(false);

    if (err) {
      showAlert(err, "error");
      setMateriais([]);
      return;
    }

    setMateriais(
      Array.isArray(data)
        ? data.filter((x) => String(x?.kind || "").toLowerCase() === "material")
        : []
    );
  };

  const carregarHistorico = async (
    materialSelecionado,
    refcentroArg = centroSelecionado?.refcentro || "1100"
  ) => {
    const seq = ++historicoSeq.current;

    const refmaterial = String(
      materialSelecionado?.refmaterial || materialSelecionado?.ref || ""
    ).trim();

    setContagens([]);
    setLoadingContagens(Boolean(refmaterial && idutilizador));

    if (!refmaterial || !idutilizador) return;

    try {
      const [data, err] = await handleApi(
        api.post("Stocks/SelInventarioHistoricoMaterial", {
          refmaterial,
          refcentro: refcentroArg,
          idutilizador,
        })
      );

      if (seq !== historicoSeq.current) return;

      if (err) {
        showAlert(err, "error");
        return;
      }

      setContagens(Array.isArray(data?.contagens) ? data.contagens : []);
    } catch (error) {
      if (seq === historicoSeq.current) {
        showAlert(error?.message || "Erro ao consultar o histórico.", "error");
      }
    } finally {
      if (seq === historicoSeq.current) {
        setLoadingContagens(false);
      }
    }
  };

  const selecionarMaterialEAN = (item) => {
    const refmaterial = String(item?.refmaterial || item?.ref || "").trim();

    if (!refmaterial) {
      showAlert("O código não devolveu uma referência de material válida.", "warning");
      return;
    }

    setMaterial({
      refmaterial,
      ref: refmaterial,
      nome: item?.nomematerial || item?.nome || "",
      title: `${refmaterial} - ${item?.nomematerial || item?.nome || ""}`,
    });

    setTextoPesquisa(`${refmaterial} - ${item?.nomematerial || item?.nome || ""}`);
    setMateriais([]);
    setOpenBarcodeReader(false);

    carregarHistorico({ refmaterial });
  };

  const fechar = () => {
    historicoSeq.current += 1;
    setLoadingContagens(false);
    setOpenBarcodeReader(false);
    setMaterial(null);
    setMateriais([]);
    setTextoPesquisa("");
    setContagens([]);
    onClose?.();
  };

  return (
    <>
      <Dialog
        open={open && !openBarcodeReader}
        onClose={fechar}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: "calc(100% - 16px)",
            maxHeight: "calc(100dvh - 24px)",
            m: 1,
          },
        }}
      >
        <DialogTitle>Histórico de contagens por material</DialogTitle>

        <DialogContent dividers>
          <MDBox display="grid" gap={2}>
            <Autocomplete
              options={centros}
              value={centroSelecionado}
              loading={loadingCentros}
              size="small"
              getOptionLabel={(option) =>
                `${option.refcentro} — ${option.centro || option.refcentro}`
              }
              isOptionEqualToValue={(option, value) =>
                String(option.refcentro) === String(value.refcentro)
              }
              onChange={(_, value) => {
                setCentroSelecionado(value);

                // Invalidar os resultados do centro anterior.
                historicoSeq.current += 1;
                setContagens([]);
                setLoadingContagens(false);

                if (material && value) {
                  carregarHistorico(material, value.refcentro);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Centro"
                  placeholder="Selecionar centro..."
                  fullWidth
                />
              )}
            />

            <MDBox display="flex" justifyContent="flex-end">
              <MDButton
                color="info"
                variant="outlined"
                startIcon={<DocumentScannerIcon />}
                onClick={() => setOpenBarcodeReader(true)}
              >
                Ler código de barras
              </MDButton>
            </MDBox>

            <Autocomplete
              options={materiais}
              value={material}
              inputValue={textoPesquisa}
              loading={pesquisando}
              filterOptions={(options) => options}
              onInputChange={(_, value, reason) => {
                if (reason === "input") {
                  pesquisarMateriais(value);
                } else {
                  setTextoPesquisa(value);
                }
              }}
              onChange={(_, value) => {
                setMaterial(value);
                carregarHistorico(value);
              }}
              getOptionLabel={(option) =>
                String(option?.title || option?.label || option?.nome || option?.ref || "")
                  .split(/\s*Stock\s*:/i)[0]
                  .trim()
              }
              isOptionEqualToValue={(option, value) =>
                String(option?.refmaterial || option?.ref || "") ===
                String(value?.refmaterial || value?.ref || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pesquisar material"
                  placeholder="Referência ou designação..."
                  size="small"
                  fullWidth
                />
              )}
            />

            {material ? (
              <MDTypography variant="button" fontWeight="bold" color="info">
                Material selecionado: {material.refmaterial || material.ref}
              </MDTypography>
            ) : (
              <MDTypography variant="caption" color="text">
                Pesquisa um material ou utiliza o leitor de código de barras.
              </MDTypography>
            )}
          </MDBox>

          {material && (
            <MDBox mt={2}>
              <ProjectTable
                tableKey="inventarios-historico-material"
                version="v1"
                title={`Últimas contagens (${contagens.length})`}
                columns={columnsHistorico}
                data={contagens}
                loading={loadingContagens}
                minWidth={1150}
                maxHeight="48vh"
                emptyMessage="Não existem contagens registadas para este material."
                defaultSortBy={[{ id: "data_contagem", desc: true }]}
              />
            </MDBox>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <MDButton color="secondary" onClick={fechar}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <DialogBarcodeReader
        open={openBarcodeReader}
        onClose={() => setOpenBarcodeReader(false)}
        onMaterialDetected={selecionarMaterialEAN}
        permitirAssociacoes
        selecionarExplicitamente
      />
    </>
  );
}
